/**
 * Designer's idle clock and draft store.
 *
 * One clock per app, built once at module load. The cookie is scoped to
 * samsurveyor.com wherever that is allowed, so Designer shares one clock with
 * Console, Explorer and Channels. Working in any one of them keeps the others
 * signed in.
 *
 * Designer is the app where the timeout can actually cost somebody something.
 * Console and Explorer mostly show saved data. Designer holds an afternoon of
 * survey structure that exists nowhere else until it is saved. That is why the
 * draft rescue in App.js matters more here than anywhere else in the product.
 */

// Deep paths on purpose. The `@cloudsam/ui` index pulls in AppShell, Panel,
// Button and the rest, so anything importing this file would drag the whole
// component set into its import graph. These modules are plain JavaScript with
// no React in them.
import { createIdleClock } from '@cloudsam/ui/dist/idle/idleClock';
import { createDraftStore } from '@cloudsam/ui/dist/idle/draftStore';
import { sessionCookieDomain } from '@cloudsam/ui/dist/idle/cookieDomain';
import { createHeartbeatSender } from '@cloudsam/ui/dist/idle/heartbeat';
import { getServiceUrl } from './sysconfig';

/** How long a person may be idle before the session ends. */
export const IDLE_LOCK_MINUTES = 30;

/** Where Designer's recovery drafts live inside the store. */
export const DRAFT_SCOPE = 'designer';

/**
 * The draft key for a survey that has never been saved to the cloud.
 *
 * A never-saved survey has no id to key on, and there can only be one of them
 * in a tab at a time, so a fixed key is right.
 */
export const NEW_SURVEY_DRAFT_KEY = 'unsaved-new-survey';

/** What an automatic version says about itself, so it is never a mystery. */
export function autoSaveNotes(at = new Date()) {
    const stamp = at.toISOString().slice(0, 16).replace('T', ' ');
    return `Automatic save before idle sign-out — ${stamp} UTC`;
}

export const idleClock = createIdleClock({
    cookiePrefix: 'sam',
    cookieDomain: sessionCookieDomain(),
    // Tells sam-dbam a person is here, so it can enforce the same 30 minutes on
    // its own. The browser timer only runs while a browser is running; close a
    // laptop at minute five and nothing revokes anything.
    //
    // The address is looked up rather than baked in, because Designer resolves
    // every service through sysconfig. A lookup that fails just means no
    // heartbeat this minute; the sender swallows it.
    onHeartbeat: createHeartbeatSender({
        url: async () => {
            const base = await getServiceUrl('sam_dbam');
            return base ? `${base.replace(/\/+$/, '')}/dbam/v1/me/heartbeat` : '';
        },
    }),
});

export const draftStore = createDraftStore();

export default idleClock;
